/*
 * Engravity: extra picture pickers on product pages (companion to engravity-badge-options.js).
 *  1. Leatherette colour options -> chips cut from the shop's patch colour photo (patch-color-swatches.jpg in
 *     Content > Files, 8 columns x 5 rows). Only kicks in when at least one colour of the product is a leatherette
 *     finish, so tumblers, badges and the like keep the theme's normal buttons. Shades the photo doesn't have
 *     (Rose, Bamboo, Cork, Blue, Blush, Light Gray) get a plain colour chip with the engraving colour as "Aa".
 *  2. "Engraving Location" option -> picture tiles from files named engraving-location-<value>.jpg
 *     (for example engraving-location-front-flap.jpg). If any image is missing the original control stays.
 *  Both drive the theme's own inputs, so the cart receives exactly what it did before.
 */
(function () {
  'use strict';

  var FILES = 'https://cdn.shopify.com/s/files/1/0526/8969/8994/files/';
  var SPRITE = FILES + 'patch-color-swatches.jpg';
  var SPRITE_COLS = 8, SPRITE_ROWS = 5;
  // Cell order in the photo, left to right, top to bottom: material-engraving colour.
  var SPRITE_KEYS = ['rawhide-black', 'rustic-gold', 'darkbrown-black', 'black-gold', 'black-silver', 'teal-black', 'lightbrown-black', 'gray-black', 'pink-black', 'purple-black', 'beige-black', 'orange-black', 'bronzecarbonfiber-black', 'goldcarbonfiber-black', 'silvercarbonfiber-black', 'bronzebrushed-black', 'goldbrushed-black', 'brownbasketball-black', 'holographic-black', 'silverbrushed-black', 'ardenbuckskin-black', 'ardensaddle-black', 'ardenkhaki-black', 'ardenpink-black', 'ardenrustic-black', 'ardenrawhide-black', 'ardenskyblue-black', 'ardenwalnut-black', 'ardenblack-black', 'ardendarkbrown-black', 'ardenolive-black', 'ardentrueblue-black', 'ardenflint-black', 'ardencharcoal-black', 'ardenbluesuede-black', 'denimgray-black', 'denimcoffee-black', 'denimlightblue-black', 'denimblue-black'];
  // Plain colours for shades the photo doesn't cover.
  var PLAIN = { rose: '#c99a9a', bamboo: '#d9b77a', cork: '#c9a877', blue: '#2a4b8a', blush: '#e8c4c0', lightgray: '#c3c8cc', lightgrey: '#c3c8cc', white: '#f4f4f4', red: '#b3202a', navy: '#172440', green: '#1a6b32', brown: '#5c3f27', walnut: '#6e4a2e', black: '#151515', gold: '#c9a646', silver: '#c3c8cc' };
  var INKS = { gold: '#d4b25a', silver: '#d9dde0', black: '#111111', white: '#ffffff' };
  var LIGHT = { white: 1, beige: 1, pink: 1, lightgray: 1, lightgrey: 1, blush: 1, bamboo: 1, cork: 1, rose: 1, rawhide: 1, lightbrown: 1, gold: 1, silver: 1 };

  function slug(s) {
    return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  // "Black-Engraves Gold", "Black/Gold", "Rustic-Engraves Gold", "Dark Brown" -> {base, ink}
  function parseColor(value) {
    var v = value.toLowerCase().replace(/\(.*?\)/g, '').replace(/engraves/g, '/').replace(/grey/g, 'gray');
    var parts = v.split(/[\/-]/).map(function (p) { return p.replace(/[^a-z]/g, ''); }).filter(Boolean);
    return { base: parts[0] || '', ink: parts[1] || '' };
  }

  // Exact material + engraving colour match first; bare material names ("Rawhide", "Gray") take the photo's
  // default cell; a named engraving colour the photo doesn't have ("Blue/Silver") gets a plain chip instead.
  function spriteIndex(color) {
    var exact = SPRITE_KEYS.indexOf(color.base + '-' + color.ink);
    if (exact > -1) return exact;
    if (color.ink) return -1;
    for (var i = 0; i < SPRITE_KEYS.length; i++) {
      if (SPRITE_KEYS[i].split('-')[0] === color.base) return i;
    }
    return -1;
  }

  // Names that only ever mean leatherette. A product needs at least one of these before any chips are drawn,
  // so a powder-coated tumbler or an acrylic badge that happens to come in "Black" or "Pink" is left alone.
  function isLeatherette(color) {
    var b = color.base;
    if (/^(rawhide|rustic|darkbrown|lightbrown|holographic|brownbasketball)$/.test(b)) return true;
    if (/^(arden|denim)/.test(b) || /(carbonfiber|brushed)$/.test(b)) return true;
    return !!color.ink && SPRITE_KEYS.indexOf(b + '-' + color.ink) > -1;
  }

  var CSS = '' +
    '.egl-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(100px,1fr));gap:8px;margin:8px 0 18px}' +
    '.egl-grid--color{grid-template-columns:repeat(auto-fill,minmax(88px,1fr))}' +
    '.egl-tile{display:flex;flex-direction:column;gap:5px;padding:5px 5px 7px;background:#fff;border:1px solid #d7dadc;border-radius:6px;cursor:pointer;font:inherit;font-size:12px;line-height:1.2;text-align:center;color:inherit;-webkit-appearance:none;appearance:none}' +
    '.egl-tile img{display:block;width:100%;aspect-ratio:1/1;object-fit:cover;border-radius:4px}' +
    '.egl-chip{display:flex;align-items:center;justify-content:center;width:100%;aspect-ratio:1/1;border-radius:4px;background-repeat:no-repeat;background-size:' + (SPRITE_COLS * 100) + '% ' + (SPRITE_ROWS * 100) + '%;font-family:Georgia,"Times New Roman",serif;font-size:22px;font-weight:700;letter-spacing:.02em}' +
    '.egl-tile:hover{border-color:#8a9297}' +
    '.egl-tile.is-selected{border-color:currentColor;box-shadow:0 0 0 1px currentColor}' +
    '.egl-tile:focus-visible{outline:2px solid currentColor;outline-offset:2px}' +
    '.egl-hidden{display:none !important}';

  function injectCss() {
    if (document.getElementById('egl-style')) return;
    var s = document.createElement('style');
    s.id = 'egl-style';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  // Describe the option's choices in one shape regardless of how the theme rendered it.
  function readChoices(opt) {
    var sel = opt.querySelector('select.product-form__single-selector');
    if (sel) {
      return {
        hide: sel.closest('.select-wrapper') || sel,
        values: Array.prototype.map.call(sel.options, function (o) { return o.value; }),
        current: function () { return sel.value; },
        choose: function (v) { sel.value = v; sel.dispatchEvent(new Event('change', { bubbles: true })); }
      };
    }
    var list = opt.querySelector('.block-swatch-list');
    if (list) {
      var inputs = list.querySelectorAll('input.block-swatch__radio');
      return {
        hide: list,
        values: Array.prototype.map.call(inputs, function (i) { return i.value; }),
        current: function () { var c = list.querySelector('input.block-swatch__radio:checked'); return c ? c.value : null; },
        choose: function (v) { Array.prototype.forEach.call(inputs, function (i) { if (i.value === v) i.click(); }); }
      };
    }
    return null;
  }

  function makeGrid(opt, label, choices, extraClass) {
    var grid = document.createElement('div');
    grid.className = 'egl-grid' + (extraClass ? ' ' + extraClass : '');
    grid.setAttribute('role', 'radiogroup');
    grid.setAttribute('aria-label', label.textContent.replace(/:.*$/, '').trim());
    function mark() {
      var cur = choices.current();
      Array.prototype.forEach.call(grid.children, function (b) {
        var on = b.getAttribute('data-value') === cur;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }
    grid.mark = mark;
    grid.show = function () {
      choices.hide.classList.add('egl-hidden');
      opt.appendChild(grid);
      mark();
    };
    document.addEventListener('variant:changed', mark);
    return grid;
  }

  function makeTile(value, choices, grid) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'egl-tile';
    b.setAttribute('role', 'radio');
    b.setAttribute('data-value', value);
    b.title = value;
    b.addEventListener('click', function () { choices.choose(value); grid.mark(); });
    return b;
  }

  // ---------- leatherette colour chips ----------
  function buildColors() {
    var opts = document.querySelectorAll('.product-form__option');
    Array.prototype.forEach.call(opts, function (opt) {
      if (opt.getAttribute('data-egl')) return;
      var label = opt.querySelector('.product-form__option-name');
      if (!label || !/colou?r/i.test(label.textContent.split(':')[0])) return;
      var choices = readChoices(opt);
      if (!choices || !choices.values.length) return;

      var parsed = choices.values.map(function (v) { var c = parseColor(v); c.sprite = spriteIndex(c); return c; });
      var leatherette = parsed.filter(isLeatherette).length;
      if (!leatherette) return; // not a leatherette product: leave the theme's buttons alone
      opt.setAttribute('data-egl', '1');

      var grid = makeGrid(opt, label, choices, 'egl-grid--color');
      choices.values.forEach(function (value, n) {
        var c = parsed[n];
        var b = makeTile(value, choices, grid);
        var chip = document.createElement('span');
        chip.className = 'egl-chip';
        if (c.sprite > -1) {
          var col = c.sprite % SPRITE_COLS, row = Math.floor(c.sprite / SPRITE_COLS);
          chip.style.backgroundImage = 'url(' + SPRITE + ')';
          chip.style.backgroundPosition = (col / (SPRITE_COLS - 1) * 100) + '% ' + (row / (SPRITE_ROWS - 1) * 100) + '%';
        } else {
          chip.style.backgroundColor = PLAIN[c.base] || '#9a9a9a';
          chip.style.color = INKS[c.ink] || (LIGHT[c.base] ? INKS.black : INKS.silver);
          chip.textContent = 'Aa';
        }
        var cap = document.createElement('span');
        cap.textContent = value;
        b.appendChild(chip);
        b.appendChild(cap);
        grid.appendChild(b);
      });
      grid.show();
    });
  }

  // ---------- engraving location tiles ----------
  function buildLocations() {
    var opts = document.querySelectorAll('.product-form__option');
    Array.prototype.forEach.call(opts, function (opt) {
      if (opt.getAttribute('data-egl')) return;
      var label = opt.querySelector('.product-form__option-name');
      if (!label || !/engraving location/i.test(label.textContent)) return;
      var choices = readChoices(opt);
      if (!choices || !choices.values.length) return;
      opt.setAttribute('data-egl', '1');

      var grid = makeGrid(opt, label, choices);
      var pending = choices.values.length, failed = false;
      choices.values.forEach(function (value) {
        var b = makeTile(value, choices, grid);
        var img = new Image();
        img.alt = '';
        img.onload = function () { if (--pending === 0 && !failed) grid.show(); };
        img.onerror = function () { failed = true; };
        img.src = FILES + 'engraving-location-' + slug(value) + '.jpg?width=400';
        var cap = document.createElement('span');
        cap.textContent = value;
        b.appendChild(img);
        b.appendChild(cap);
        grid.appendChild(b);
      });
    });
  }


  // ---------- sticky add-to-cart bar (phones and tablets) ----------
  var BAR_CSS = '' +
    '.egs-bar{position:fixed;left:0;right:0;bottom:0;z-index:5;background:#fff;border-top:1px solid var(--border-color,#e1e3e4);box-shadow:0 -4px 16px rgba(0,0,0,.08);padding:10px 16px calc(10px + env(safe-area-inset-bottom));transform:translateY(110%);visibility:hidden;transition:transform .25s ease,visibility .25s}' +
    '.egs-bar.is-visible{transform:none;visibility:visible}' +
    '.egs-bar__inner{display:flex;align-items:center;gap:12px;max-width:640px;margin:0 auto}' +
    '.egs-bar__img{width:44px;height:44px;object-fit:cover;border-radius:6px;flex:none;background:#f3f5f6}' +
    '.egs-bar__text{flex:1 1 auto;min-width:0}' +
    '.egs-bar__title{font-size:13px;line-height:1.25;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:var(--heading-color,#1c1b1b)}' +
    '.egs-bar__price{font-size:15px;font-weight:600;color:var(--heading-color,#1c1b1b)}' +
    '.egs-bar__btn{flex:none;min-height:42px;padding:0 18px}' +
    'body.egs-bar-open .egt-top{bottom:92px}' +
    // lift the Shopify Forms discount teaser above the bar (its wrapper padding is a CSS variable on the host)
    '@media (max-width:999px){body.egs-bar-open shopify-forms-embed{--app-embed-padding-y:100px}}' +
    '@media (min-width:1000px){.egs-bar{display:none!important}body.egs-bar-open .egt-top{bottom:28px}}';

  function priceText(el) {
    if (!el) return '';
    return Array.prototype.map.call(el.childNodes, function (n) {
      return n.nodeType === 1 && n.classList.contains('visually-hidden') ? '' : n.textContent;
    }).join('').replace(/\s+/g, ' ').trim();
  }

  function buildStickyBar() {
    if (document.querySelector('.egs-bar')) return;
    var form = document.querySelector('form.product-form');
    var btn = form && form.querySelector('.product-form__add-button');
    var anchor = form && form.querySelector('.product-form__payment-container');
    if (!btn || !anchor) return;
    var titleEl = document.querySelector('.product-meta__title');
    var img = document.querySelector('.product-gallery__carousel-item.is-selected img, .product-gallery__carousel-item img, .product-gallery img');

    var style = document.createElement('style');
    style.textContent = BAR_CSS;
    document.head.appendChild(style);

    var bar = document.createElement('div');
    bar.className = 'egs-bar';
    var thumb = img ? '<img class="egs-bar__img" src="' + (img.currentSrc || img.src) + '" alt="">' : '';
    bar.innerHTML = '<div class="egs-bar__inner">' + thumb +
      '<div class="egs-bar__text"><div class="egs-bar__title"></div><div class="egs-bar__price"></div></div>' +
      '<button type="button" class="egs-bar__btn button button--primary"></button></div>';
    document.body.appendChild(bar);

    var bTitle = bar.querySelector('.egs-bar__title');
    var bPrice = bar.querySelector('.egs-bar__price');
    var bBtn = bar.querySelector('.egs-bar__btn');
    bBtn.addEventListener('click', function () { btn.click(); });

    function sync() {
      bTitle.textContent = titleEl ? titleEl.textContent.trim() : document.title;
      var p = document.querySelector('form.product-form .price-list .price--highlight, form.product-form .price-list .price');
      bPrice.textContent = priceText(p);
      bBtn.textContent = btn.textContent.trim() || 'Add to cart';
      bBtn.disabled = btn.disabled;
    }
    var raf = 0;
    function queue() { if (raf) return; raf = requestAnimationFrame(function () { raf = 0; sync(); }); }
    sync();
    new MutationObserver(queue).observe(form, { subtree: true, childList: true, characterData: true, attributes: true });
    document.addEventListener('variant:changed', queue);

    // Show the bar once the real Add to cart button has scrolled up out of view.
    var shown = null;
    function check() {
      var past = anchor.getBoundingClientRect().bottom < 0;
      if (past === shown) return;
      shown = past;
      bar.classList.toggle('is-visible', past);
      document.body.classList.toggle('egs-bar-open', past);
    }
    var ticking = false;
    function onScroll() { if (ticking) return; ticking = true; requestAnimationFrame(function () { ticking = false; check(); }); }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    new IntersectionObserver(check, { threshold: 0 }).observe(anchor);
    check();
  }

  function build() {
    injectCss();
    buildColors();
    buildLocations();
    buildStickyBar();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', build); else build();
})();
